/** Discovery rows: Live / Tonight / Latest finals / Following / Classics, each card directable. */
import type { CSSProperties } from 'react';
import { directorTeamName, type DirectorLanguage } from '@clutch/core';
import { TeamLogo } from '../ui/Media';
import { teamAccent } from '../ui/color';
import { pick } from '../director/i18n';
import type { FeedCard } from './feed';
import { cardDate, cardStatus } from './language';
import type { DiscoveryRow } from './featured';

const ROW_COPY: Record<DiscoveryRow['id'], { en: string; hi: string; zh: string }> = {
  live: { en: 'Live', hi: 'लाइव', zh: '直播中' },
  tonight: { en: 'Tonight', hi: 'आज रात', zh: '今晚' },
  finals: { en: 'Latest finals', hi: 'ताज़ा नतीजे', zh: '最新完赛' },
  following: { en: 'Following', hi: 'फ़ॉलो कर रहे हैं', zh: '关注' },
  classics: { en: 'Classics', hi: 'क्लासिक', zh: '经典' },
};

export function DiscoveryRows({ rows, lang, busy, onDirect }: { rows: readonly DiscoveryRow[]; lang: DirectorLanguage; busy?: string; onDirect: (c: FeedCard) => void }) {
  return (
    <div className="dr" data-testid="discovery-rows">
      {rows.map((row) => (
        <section key={row.id} className="dr__row" aria-label={pick(ROW_COPY[row.id], lang)} data-testid={`row-${row.id}`}>
          <h2 className="dr__h">{row.id === 'live' && <i className="dr__dot" aria-hidden="true" />}{pick(ROW_COPY[row.id], lang)}</h2>
          <div className="dr__scroll">
            {row.cards.map((g) => <GameCard key={`${row.id}-${g.key}`} g={g} lang={lang} busy={busy === g.key} disabled={!!busy} onDirect={() => onDirect(g)} />)}
          </div>
        </section>
      ))}
    </div>
  );
}

function GameCard({ g, lang, busy, disabled, onDirect }: { g: FeedCard; lang: DirectorLanguage; busy: boolean; disabled: boolean; onDirect: () => void }) {
  const style = { '--a': teamAccent(g.away.tricode), '--h': teamAccent(g.home.tricode) } as CSSProperties;
  const scored = g.away.score !== undefined && g.home.score !== undefined && g.kind !== 'tonight';
  const done = g.kind === 'final' || g.kind === 'classic';
  const awayWon = scored && done && g.away.score! > g.home.score!;
  const homeWon = scored && done && g.home.score! > g.away.score!;
  const pre = g.kind === 'tonight';
  return (
    <article className={`dcard dcard--${g.kind}`} style={style} data-testid="discovery-card" data-kind={g.kind} data-game-id={g.gameId}>
      <span className="dcard__status">
        {g.kind === 'live' && <i className="dr__dot" aria-hidden="true" />}
        {g.kind === 'live' ? 'LIVE · ' : ''}{cardStatus(g, lang)}
        {g.kind !== 'classic' && <small>{cardDate(g, lang)}</small>}
      </span>
      <span className={`dcard__team${awayWon ? ' is-win' : ''}`}><TeamLogo tricode={g.away.tricode} size={28} /><b>{directorTeamName(g.away.tricode, lang)}</b>{scored && <strong>{g.away.score}</strong>}</span>
      <span className={`dcard__team${homeWon ? ' is-win' : ''}`}><TeamLogo tricode={g.home.tricode} size={28} /><b>{directorTeamName(g.home.tricode, lang)}</b>{scored && <strong>{g.home.score}</strong>}</span>
      {g.headline && <span className="dcard__line">{g.headline}</span>}
      <button className="dcard__go" onClick={onDirect} disabled={disabled} data-testid="direct-game"
        aria-label={`${pre ? pick({ en: 'Join', hi: 'जुड़ें', zh: '加入' }, lang) : pick({ en: 'Direct this game', hi: 'यह मैच डायरेक्ट करें', zh: '导演这场比赛' }, lang)}: ${directorTeamName(g.away.tricode, lang)} @ ${directorTeamName(g.home.tricode, lang)}`}>
        {busy ? pick({ en: 'Opening…', hi: 'खुल रहा है…', zh: '正在打开…' }, lang) : pre ? pick({ en: 'Join before tip', hi: 'टिप से पहले जुड़ें', zh: '赛前加入' }, lang) : pick({ en: 'Direct this game', hi: 'यह मैच डायरेक्ट करें', zh: '导演这场比赛' }, lang)}
      </button>
    </article>
  );
}
